// WP4 implementation tuning (docs/decisions/WP4.md). The §10 numbers the spec names live in
// `TUNE.bot` (constants.ts); this file holds the bot implementation's own feel numbers, the way
// every map file keeps its hazard numbers in a `TUNING` const (§17.2). Distances in meters, times
// in ticks unless the name says Sec, speeds as stick magnitudes (0..1) unless the name says m/s.
import { Difficulty } from '../ids';

/** Per-difficulty triple, indexed by `Difficulty` (CHILL, NORMAL, SPICY). */
type ByDifficulty = readonly [number, number, number];

export const BOT_TUNE = {
  /** Solid scratch capacity for `MapDef.solids` reads. */
  maxSolids: 64,
  /** Static capsules at least this tall are rails a pushed bean cannot cross (safety field walls). */
  wallMinTop: 0.5,
  /** Extra wall thickness around rails in field cells. */
  wallPadCells: 0.5,
  /** Static solids at least this tall block walking; the blocked mask pads them by blockPad. */
  blockMinTop: 0.3, blockPad: 0.3, hazardBlockPad: 0.65,
  /** The edge margin never exceeds this share of the safest ground left (shrunken arenas, islands). */
  marginCapFrac: 0.5, marginFloor: 0.5,
  /** Edge margin multiplier while the bot's own ledge is used up (cdLedge, §2.9). */
  usedLedgeMarginMult: 1.5,
  /** RECOVER margin while a hazard reflex (arm hop) owns the stick. */
  hazardRecoverMargin: 0.7,
  /** Edge margin per map (by MapId): Spin Cycle's arms throw people outward, so stay further in. */
  mapMargin: [1, 1, 1, 1, 1, 1.3] as number[],

  /** Crumble Cake (§6.4): every step cracks a tile, candle tiles hold until Sudden Death. */
  cake: {
    /** A cracking tile counts as void for planning once this few ticks remain (fresh cracks are still floor). */
    crackVoidTicks: 26,
    /** Safety field rebuild cadence on the cake (tile timers change without events). */
    rebuildTicks: 10,
    /** Where to stand on a candle tile: this far from the candle, toward the cake center. */
    candleStandOff: 0.92,
    /** Anchor choice: travel cost per meter, crowding penalty per bean near the spot, flicker penalty. */
    travelWeight: 0.25, crowdRadius: 1.4, crowdWeight: 2, flameWeight: 10, flameNotice: 0.6, flamePlanTicks: [100, 300, 400] as ByDifficulty, awayCap: 7, centerBonus: 1.5, claimWeight: 3,
    /** Hop a gap when the recover path is void this close ahead but ground lies within reach. */
    gapNear: 1.0, gapFar: 3.2, gapMinSpeed: 3.5,
    /** Travel by hops (one cracked tile per hop instead of two per 3.8 m walked). */
    hopTravelMin: 3.0, hopMinSpeed: 5.0, hopLandSafety: 0.3,
    /** Island travel: path sampling, the widest gap worth a hop, the brink distance and run-up speed. */
    holdRadius: 0.9, pathStep: 0.25, pathVoid: 0.05, hopMaxGap: 2.4, brink: 0.3, hopMinRunSpeed: 4.0, travelMargin: 0.45,
    routeTicks: 15, routeGoalSlack: 1.0, legArrive: 0.4, runUp: 0.6,
  },

  /** Spin Cycle (§6.5): riding the gap between the sweeper arms (bots/mapTactics.ts `spinGapSpot`). */
  spin: {
    /** The gap spot is handed to the motor this many ticks ahead (it moves with the arms). */
    leadTicks: 18,
    /** Candidate radii (m) and fractions across the gap (0 = the arm behind, 1 = the arm ahead). */
    radii: [1.9, 2.6, 3.3, 4.0] as readonly number[], fracs: [0.3, 0.5, 0.7] as readonly number[],
    /** Skip radii where the gap outruns this share of our run speed. */
    maxTrackFrac: 0.8,
    threatPredictSec: 0.5, threatMin: 0.8,
    clearCap: 2.5, clearWeight: 0.6, safetyCap: 1.5, safetyWeight: 1.0, travelWeight: 0.1,
  },

  perceive: {
    /** Fraction of the reaction delay others are extrapolated by their velocity (§10.1: half). */
    extrapolateFrac: 0.5,
  },

  gary: {
    /** Gary blunders like any Chaos Goblin (the personality already doubles it). */
    blunderMult: 1,
    /** The goose honk is Gary's whole personality. */
    honksPerMin: 10,
  },

  /** Hazard forecast (bots/hazards.ts). */
  hazard: {
    dangerCell: 1.0, dangerRefreshTicks: 6,
    ballMargin: 0.35, ballHorizonTicks: 40,
    armSoonTicks: 12, armMinCloseFrac: 0.35,
    bubblePopRadius: 1.5, bubbleMargin: 0.3, bubbleJumpWobble: 0.55,
    flameRadius: 2.0, flameMargin: 0.6,
  },

  /** Motor (bots/motor.ts): context steering, stick smoothing, fidgets. */
  motor: {
    dirCount: 16,
    velProbeSec: 0.15, probeNear: 0.6, probeFar: 1.4,
    edgeWeight: 2.2, edgeHardSafety: 0.35, edgeHardPenalty: 3,
    dangerWeight: 1.6,
    crowdProbe: 0.8, crowdRadius: 1.0, crowdWeight: 0.35,
    fidgetMinTicks: 45, fidgetMaxTicks: 100, fidgetStepMinTicks: 12, fidgetStepMaxTicks: 24, fidgetRandom: 0.6, fidgetSpeed: 0.42,
    arriveStop: 0.12, arriveDist: 0.6,
    minSpeed: 0.12, minMove: 0.26,
    blockedPenalty: 0.8, launchPenalty: 1.2,
    /** Banana peels on the floor: steer around them (weight by difficulty, how wide a berth). */
    peelPenalty: [0.5, 1.2, 1.6] as ByDifficulty, peelBerth: 0.3,
    stuckStick: 0.4, stuckSpeed: 0.4, stuckTicks: 24, unstickTicks: 20, unstickSpeed: 0.8,
    idleInwardFrac: 0.8, idleInwardSpeed: 0.45,
    exactTolerance: 0.15, dirHysteresis: 0.2,
    escapeSpeed: 0.8,
    wobbleMinDist: 2.5, wobbleDeg: 10, wobbleRateMin: 0.03, wobbleRateMax: 0.07,
    urgentSmoothing: 0.7, keepMagnitudeMin: 0.3,
    postShoveStick: 0.55,
    heldTicks: 3,
    grabNoiseMult: 0.6,
  },

  /** Everything that makes a bot read as a person (bots/tactics.ts, states.ts, itemTactics.ts). */
  feel: {
    individualSpread: 0.12,
    stickSmoothing: [0.24, 0.34, 0.48] as ByDifficulty,
    anticipateChance: [0.04, 0.14, 0.3] as ByDifficulty,
    commitJitterTicks: [16, 10, 5] as ByDifficulty,
    baitChance: [0, 0.18, 0.32] as ByDifficulty,
    teeterHopChance: [0.12, 0.22, 0.3] as ByDifficulty,
    tacticianBaitMult: 1.8,
    tacticianMindGameBonus: 0.15,

    // RECOVER
    teeterJitterTicks: 4, windmillRate: 0.45, windmillSpeed: 0.9,
    stomperClosing: 0.4, mindGameTrigger: 0.4, ledgeTimeoutPad: 10, mindGameMinWait: 10, cakeLedgeJitterTicks: 8,
    /**
     * A hanger waits out an enemy standing within `lurkRange` of where the climb comes up (it would
     * shove the climber straight back off with the ledge used up) with this chance per hang, by
     * difficulty, and climbs once it leaves or commits to a stomp.
     */
    ledgePatience: [0.15, 0.55, 0.85] as ByDifficulty, lurkRange: 1.7,
    /** Spin Cycle: climb when an arm will reach the hands within this many ticks. */
    ledgeArmLead: 45, ledgeArmSlack: 4,
    /** Ghost pebbles: chance to notice the shadow (by difficulty), escape step and margin. */
    pebbleNotice: [0.45, 0.7, 0.9] as ByDifficulty, pebbleStep: 1.2, pebbleDodgePad: 0.25, cubeDropZ: 0.6,
    mashJitterMin: 0.8, mashJitterMax: 1.25,
    tumbleAirSec: 0.3, tumbleSaveSafety: 0.3, diProbe: 2.0, diSide: 1.0, crownDiSafety: 0.8, crownDiMin: 0.3,
    plungerSaveDistWeight: 0.15, plungerSaveBelowZ: -0.25,
    floatExtraSafety: 1.0, floatDriftSpeed: 0.6,
    /** Parachute landing: usable share of the drift reach, landing safety wanted, search grid and weights. */
    paraReachFrac: 0.85, paraLandSafety: 0.5, paraRings: 4, paraDirs: 12, paraSafetyCap: 1.2, paraVoidWeight: 4, paraGoalWeight: 0.12,
    recoverBand: 0.25, recoverExit: 0.35, recoverProbeSec: 0.1,

    // CARRY / SPUN / WINDUP / climb payoff
    carryThreatRange: 1.5, carryMaxTicks: 110, carryMarginFrac: 0.4,
    flingAimFromTicks: 60, flingTargetRange: 6, flingDirs: 12,
    gloveTrackRange: 3, windupStick: 0.6,
    payoffTicks: 30, payoffRange: 1.8, payoffVictimSafety: 1.2,
    /**
     * After a climb (ledge used up, §2.9): get this far from the rim (m, capped like the edge
     * margin on small ground) within this many ticks before playing on.
     */
    climbRetreatSafety: 2.2, climbRetreatTicks: 90,

    // ATTACK / FLANK
    minGradient: 0.05, pushFarSafety: 4.0, pushNearSafety: 1.5,
    safePointSteps: 6, safePointStep: 0.5,
    lungeArmSlackTicks: 6, lungeFollowTicks: 8, lungeSpread: 1.5, lungeSpreadCrown: 2.5, crownArmClearTicks: 30,
    /** Moving hazards on a straight path: samples, a ball's passage (ticks) and the slack after our visit. */
    pathSamples: 4, ballPassTicks: 26, ballSlackTicks: 10, lungeEndSafety: 0.2, lungeFollowThrough: 0.5, crownLungeEndSafety: 1.2,
    /** Rim shoves (§2.9: the lunge ends in a teeter): taken for a target within rimLungeRange when nobody else is within
     * teeterThreatRange of where we would teeter; otherwise only on a greed roll (by difficulty, re-rolled every greedRollTicks). */
    rimLungeRange: 1.6, teeterThreatRange: 3.0, edgeGreed: [0.35, 0.12, 0.04] as ByDifficulty, greedRollTicks: 60,
    shoveLeadMaxSec: 0.12, shoveLeadFrom: 0.6,
    helplessRange: 2.0, grabRollTicks: 60,
    chaosShoveChance: 0.45, chaosRethinkTicks: 24,
    punishMinCd: 15, punishAlign: 0.55,
    shoveSafetyFrac: 0.6, attackMarginMult: 0.6,
    standoffDist: 1.6, backOffStep: 1.0, backOffSpeed: 0.5, standoffSpeed: 0.6, standoffArrive: 0.5,
    baitFacingCos: 0.8, baitMaxDist: 3.0, baitOrbitRad: 0.6, baitDist: 2.3, baitSpeed: 0.55, baitTicks: 45,
    behindCos: 0.5, routeDist: 3.5, circleSwitch: 0.25, routeStepRad: 0.9,
    closeDist: 3.0, closeSpeed: 0.8, approachArrive: 0.35,
    stompInset: 0.3, stompSlowDist: 1.2, stompSpeed: 0.55, stompArrive: 0.2,
    /** Hover ring (just outside a lunge's reach) and the openings that end the hover. */
    hoverDist: 3.0, hoverSpeed: 0.55, hoverRate: 0.035, hoverSwing: 0.6,
    awayCos: -0.5, edgeOpening: 1.4, dizzyOpening: 45, interiorAlign: 0.35,
    /** Ticks of hovering before committing without an opening: [min, max] by difficulty, × personality. */
    patienceMin: [90, 70, 50] as ByDifficulty, patienceMax: [240, 200, 160] as ByDifficulty,
    patienceMult: [0.6, 1.1, 1.6, 0.35, 1.2] as readonly number[],
    /** Range misjudgment per approach (σ, meters) by difficulty: early lunges whiff. */
    rangeErrSigma: [0.45, 0.3, 0.15] as ByDifficulty,
    /** Weight on hazard danger in steering, positioning and EVADE (by difficulty). */
    hazardRespect: [0.55, 1, 1.25] as ByDifficulty,

    // WANDER / ITEM / EVADE
    wanderCandidates: 8, wanderSafetyCap: 1.5, wanderRingWeight: 0.35, wanderDangerWeight: 3, wanderTravelWeight: 0.08,
    wanderRandom: 0.4, wanderCrowdRadius: 1.8, wanderCrowdWeight: 0.8, wanderKeepBonus: 0.3, wanderRepickTicks: 150,
    wanderArrive: 0.6, wanderLingerTicks: 40, wanderMinSafetyFrac: 0.8, wanderSpeed: 0.7, wanderMoveOnDist: 1.5,
    /** Holding a map anchor (Cake candle tile): arrive radius and the margin while there. */
    anchorArrive: 0.35, anchorMargin: 0.2,
    /** Spin Cycle wander ring around the hub (Calm, then Ramp and Sudden Death). */
    spinWanderCalm: 3.0, spinWanderRamp: 1.5,
    crateArrive: 0.1, crateWaitPad: 0.85,
    safeSpotMinSafety: 0.5, safeSpotDangerWeight: 4, safeSpotTravelWeight: 0.15,
    bombClearPad: 0.8,

    // Reflexes
    threatCos: 0.7, sidestepProbe: 1.2, sidestepBack: 0.35, sidestepTicks: 14, hopLandSafety: 0.4, hopArmSlack: 6,
    sidestepSafetyCap: 2.0, sidestepHazardPenalty: 3, sidestepMinScore: 0.3,
    anticipateJumpMult: 0.5, anticipateRange: 2.6, anticipateClosing: 2.0, anticipateCos: 0.85,
    crownAlertMult: 3, crownAnticipateClosing: 0.2, crownPatienceMult: 2.0, crownCloseOut: 1.0,
    /** Targets running faster than this are not easy openings, and need a closer commit. */
    runnerSpeed: 3.5, runnerRangeCut: 0.8,
    armWatchTicks: 40, bubbleHopChance: 0.6, bubbleRollTicks: 60, gapRideReflexTicks: 10,
    /** A hop stays above the 0.55 m arm top for ~23 ticks from tick 6: clear crossings up to armAirTicks, hop armJumpLead before contact. */
    armAirTicks: 21, armJumpLead: 6, armHopRise: 6, armHopAir: 23, armPanicLead: [2, 5, 6] as ByDifficulty, armRunTicks: 34, armRunOutward: 0.3, armLeanTicks: 14,
    armEscapeTicks: 26, armHopSteerTicks: 16, armHubRadius: 3, armRimRadius: 5.2,

    // Items
    itemGapTicks: 30, itemBoredTicks: 480, gloveBoredAlignDeg: 35, gloveBoredExposure: 0.35,
    bananaBehindCos: 0.5, bananaChaseSpeed: 2.0, bananaChokeRange: 4, bananaGapTicks: 40,
    cowardCorneredSafety: 1.5, gloveEdgeSafety: 1.5,
    balloonDodgeStep: 0.6, balloonTrappedSafety: 0.5,
    cowardPlungerMin: 4, plungerMin: 2.5, plungerBackProbe: 1.5, plungerAlign: 0.6, plungerEdgeSafety: 2.0,
    cowardBombMin: 5, bombExposedSafety: 1.5, chaosCrowdBonus: 1, bombExposedScore: 1.2, bombThrowChance: 0.5,
    /**
     * Whether a bot notices a lit bomb near it at all, rolled once per bomb by difficulty (×
     * `bombBusyNotice` mid-fight or off its feet), like the pebble shadows. Bots used to flee every
     * bomb on time: 1 bomb hit in 40 throws on Teacup, so a human's bombs were harmless (INT-A).
     * An unnoticed bomb still makes it flee once its fuse is down to `bombPanicFuse`.
     */
    bombNotice: [0.5, 0.7, 0.9] as ByDifficulty, bombBusyNotice: 0.6, bombPanicFuse: 24,
    /** A thrown bomb lands with this much floor around it: stepped toward the thrower, at most steps × step (m). */
    bombLandSafety: 0.7, bombLandStep: 0.25, bombLandSteps: 6,
    bombRegrabMinFuse: 50,
  },

  /** Utility selection (bots/think.ts) beyond the §10.2 formula. */
  think: {
    /** WANDER/POSITION baseline utility, plus the disengage bonus after the bot's own shove. */
    wanderU: 0.7, fewWanderMult: 0.72, disengageTicks: 75, disengageU: 0.3, lowSafetyWanderU: 0.2, usedLedgeWanderU: 0.3,
    /** Attack interest ramps from warmupStart to 1 over the first warmupTicks of a round. */
    warmupTicks: 240, warmupStart: 0.35,
    /** Attack interest per map (by MapId): hazard maps take attention away from fighting. */
    mapAggression: [1, 1, 0.85, 0.75, 0.6, 0.55] as number[],
    /** A second hunter already on the target (the hard cap is TUNE.bot.maxPerTarget). */
    sharedTargetMult: 0.75,
    /** EVADE utilities: a landed bomb, standing in predicted danger. */
    evadeBombU: 3, evadeDangerU: 2.5,
    /** Bully "prefers the strongest target": bonus per point of match-score lead over the field. */
    strongestPerPoint: 0.05, strongestMax: 0.6,
    /** Retaliation: a bean that just hit this bot is more interesting for a while. */
    retaliateTicks: 180, retaliateMult: 1.35,
    /** Humans are not special targets, but bots near one fight each other 30% of the time (§10.6). */
    brawlRollTicks: 300,
    /** Attack interest falls off when the bot's own footing is bad. */
    ownSafetyLow: 1.0, ownSafetyLowMult: 0.6, lowSafetyFrac: 0.6,
    /** Late-round boredom: rounds should end, so aggression creeps up with the phase. */
    rampAggression: [1, 1, 1.15, 1.15, 1.15, 1] as number[], sdAggression: [1, 1, 1.35, 1.35, 1.35, 1.2] as number[],
    /** Danger notice delay = |N(0,1)| × hazard timing σ × this; duel (2 left) attack interest. */
    launchRayBias: 2.5, boredAfterTicks: 1800, boredRampTicks: 1800, boredAggression: 0.6,
    noticeSigmaMult: 2, duelAggression: 1.3, duelDelayTicks: 600, duelRampTicks: 1500,
    /** A loose bomb worth picking up and throwing back. */
    bombGrabU: 1.4, bombGrabRange: 3,
    /** Gary's teacup ride: chance per think while nothing better is going on, and the cooldown. */
    cupChance: 0.01, cupCooldownTicks: 1800,
  },

  /** Social layer (bots/social.ts): honks, emotes, dances. */
  social: {
    honkBackRange: 9, honkBackMinTicks: 15, honkReplyCooldownTicks: 180,
    emoteDelayMin: 20, emoteDelayMax: 70,
    danceTicks: 150,
    introHonkChance: 0.15,
  },

  /** Mode tactics (bots/modeTactics.ts). */
  modes: {
    potatoKeepAwayPerIntensity: 0.12,
    potatoProxMin: 0.15, potatoProxRange: 12, potatoGiverMult: 0.3, potatoPassU: 2.0,
    potatoCarrierAttackMult: 0.2, potatoFleeBuffer: 1.5, potatoFleeU: 1.5, potatoAttackMult: 0.5,
    potatoShoveRange: 1.7, potatoLeadMaxSec: 0.4, potatoArrive: 0.0, potatoCarrierMarginMult: 0.6, potatoUnreachableTicks: 90, potatoCakeAnchorFlee: false as boolean,
    fleeRepickTicks: 12, fleePredictSec: 0.4, fleeCandidates: 12, fleeStep: 2.0, fleeMinSafetyFrac: 0.7,
    fleeDistCap: 4, fleeDistWeight: 1.0, fleeSafetyCap: 1.5, fleeSafetyWeight: 1.0, fleeDangerWeight: 3, fleeArrive: 0.4,

    crownHoldU: 3, crownHolderAttackMult: 0.3, crownEscortAttackMult: 0.8, crownChaseU: 0.5, crownLeaderBoost: 1.0,
    crownOtherAttackMult: 0.6, crownFetchU: 2.2, crownFetchMin: 0.3, crownFetchRange: 12, crownFetchMargin: 0.6, crownFetchArrive: 0.1,
    crownRetargetMin: 30, crownRetargetMax: 75, crownPopReactMin: 8, crownPopReactMax: 28, crownRegrabMult: 1.5, crownHesitateStick: 0.3, crownRevengeTicks: 300, crownRevengeMult: 2.5, crownDiTicks: 60, crownMobRange: [3.5, 3.5, 4.5, 3.5, 3.5, 3.5] as number[],
    /**
     * How long a fresh wearer gets mobbed, by MapId. Crumble Cake 150 (INT-A): with only beans
     * popping the crown, its island fights produced too few pops (4.7–6.2 per round over 4 seeds,
     * now 6.1–7.4) while 84–88 % of rounds still end at the goal.
     */
    crownFreshTicks: [90, 90, 180, 90, 150, 90] as number[], crownMobMult: 4,
    crownLoiterU: 1.1, loiterRepickTicks: 40, loiterCandidates: 10, loiterCrowd: 2.0, loiterCrowdWeight: 1.5, loiterTravelWeight: 0.1, loiterSpeed: 0.75,
    /**
     * Bots chasing the crown wearer at once, by MapId (≤ TUNE.bot.maxPerTarget). Spin Cycle is 1
     * since hazards stopped popping the crown (INT-A): two chasers spread the crown time so thin that
     * a sixth of the rounds ran to the cap (goal 81 → 84 % over 300 rounds). One chaser at a time
     * plus the fresh-wearer scramble (`crownFreshTicks`) is what keeps a round a tug of war.
     */
    crownChasers: [2, 2, 1, 2, 1, 1] as number[],
    /** Crown play by personality (BULLY, OPPORTUNIST, COWARD, CHAOS, TACTICIAN): wearer's flight from threats, wearer's shove range, vulture ring. */
    crownStyle: [
      { threat: 0.7, shove: 1.6, loiter: 4.5 }, { threat: 1.0, shove: 1.4, loiter: 3.5 }, { threat: 1.6, shove: 1.0, loiter: 5.5 },
      { threat: 0.8, shove: 1.8, loiter: 4.5 }, { threat: 1.2, shove: 1.4, loiter: 4.5 },
    ] as readonly { threat: number; shove: number; loiter: number }[], holdRepickTicks: 12, holdCandidates: 12, holdStep: 2.5, holdStepFar: 4.5, holdPredictSec: 0.5, holdThreatMin: 0.8,
    holdSafetyCap: 2.0, holdSafetyWeight: 1.0, holdThreatWeight: 3.0, holdDangerWeight: 6, holdArrive: 0.4, holdPadClear: 0.35, holdSpinHubWeight: 0.5, holdLongAheadTicks: 120, holdLongDangerWeight: 3,

    ghostEdgeBonus: 10, ghostSafetyCap: 4, ghostGrudgeMult: 1.5, ghostHumanMult: 1.2, ghostTravelWeight: 0.15, ghostKeepBonus: 1.0,
    ghostIdleRate: 0.01, ghostIdleRadius: 3, ghostIdleSpeed: 0.5, ghostLeadDamp: 1.0, ghostEdgeSafety: 0.4,
    ghostDizzyDrop: 60, ghostDizzySafety: 1.0,
    ghostAimSigma: [0.8, 0.55, 0.3] as ByDifficulty, ghostAimTicks: 60, ghostHazardDrop: 0.6, ghostImpatientTicks: 600, ghostArrive: 0.2, ghostCircleRate: 0.04, ghostCircleRadius: 1.2, ghostCircleSpeed: 0.5,

    lobby: {
      strollWeight: 3, dummyWeight: 3, vendWeight: 2, readyWeight: 1.5, socialWeight: 2, sparWeight: 2, cardWeight: 1,
      taskMinTicks: 240, taskMaxTicks: 600, sparRange: 6, sparU: 1.5, taskU: 1.0, attackMult: 0.2, humanSparTicks: 600,
      dummyShoveRange: 1.3, dummyStandoff: 1.1, walkSpeed: 0.65,
      /** §6.1 pads: vending trigger, READY pad, vote cards. */
      vend: { x: 0, y: -4.5 }, readyPad: { x: 0, y: 3.2 }, cards: [{ x: -5, y: -2.8 }, { x: 0, y: -2.8 }, { x: 5, y: -2.8 }],
      padArrive: 0.5, socialDist: 2.2, socialSlack: 0.6, socialEmoteChance: 0.02,
    },
    podium: {
      defendU: 1.5, attackMult: 0.4, standMinTicks: 120, standMaxTicks: 360, joinChance: 0.4, dethroneU: 1.2,
      defendShoveRange: 1.5, homeSlack: 0.8, pedestalMinZ: 0.2, homeSpeed: 0.6, shoveRange: 1.5, jumpFrom: 3.55, jumpMin: 3.15, runUpFrom: 4.5, jumpSpeed: 5.5, climberDz: 0.35, accelRoom: 0.35, showOffChance: 0.004,
    },
    dance: {
      repickTicks: 60, candidates: 10, searchRadius: 4, edgeSafety: 1.0, travelWeight: 0.2, arrive: 0.5,
      walkSpeed: 0.7, wiggleRate: 0.35, wiggleSpeed: 0.35, emoteEvery: 45,
    },
    /** Gary's teacup ride (§6.2): run in from runUpRadius and jump at jumpRadius to clear the 0.8 m wall. */
    cup: { maxTicks: 300, runUpRadius: 5, runUpSlack: 0.5, runStick: 1, jumpRadius: 2.9 },
  },
} as const;

/** Reads a per-difficulty triple (unknown difficulties play as Normal). */
export function byDifficulty(t: ByDifficulty, d: Difficulty): number {
  return t[d] ?? t[Difficulty.NORMAL];
}
